import React from 'react';
import clsx from 'clsx';
import {
	FormControlLabel,
	type FormControlLabelProps,
	Checkbox as MuiCheckbox,
	checkboxClasses as muiCheckboxClasses,
	type CheckboxProps as MuiCheckboxProps,
} from '@mui/material';
import {
	ErrorField,
	type ErrorFieldProps,
} from '@netx/core-components/ErrorField';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import {
	CHECKBOX_CLASS_NAME,
	checkboxClasses,
	type CheckboxClasses,
	getCheckboxUtilityClass,
} from './checkboxClasses';

type CheckboxOwnerState = WithClasses<
	CheckboxClasses,
	{
		psuedoDisabled?: boolean;
		indeterminateUnchecked?: boolean;
	}
>;

const useUtilityClasses = (ownerState: CheckboxOwnerState) => {
	const {classes, indeterminateUnchecked, psuedoDisabled} = ownerState;
	const slots = {
		root: [
			'root',
			psuedoDisabled && 'psuedoDisabled',
			indeterminateUnchecked && 'indeterminateUnchecked',
		],
		checkbox: ['checkbox'],
		error: ['error'],
		formControlLabel: ['formControlLabel'],
	};
	return composeClasses(slots, getCheckboxUtilityClass, classes);
};

const CheckboxRoot = styled('div', {
	name: CHECKBOX_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	display: 'inline-flex',
	flexDirection: 'column',
	[`&.${checkboxClasses.psuedoDisabled} .${muiCheckboxClasses.root}`]: {
		color: theme.palette.action.disabled,
	},
	[`&.${checkboxClasses.indeterminateUnchecked} .${muiCheckboxClasses.indeterminate}`]:
		{
			color: theme.palette.text.secondary,
			'& svg': {
				display: 'none',
			},
			'&::before': {
				content: '""',
				display: 'block',
				height: 18,
				width: 18,
				border: `2px solid currentColor`,
				borderRadius: 2,
				margin: 3,
			},
			'&::after': {
				content: '""',
				display: 'block',
				height: 2,
				position: 'absolute',
				left: '50%',
				right: '50%',
				top: '50%',
				marginTop: -1,
				marginLeft: -4,
				marginRight: -4,
				backgroundColor: 'currentColor',
			},
		},
}));

type ExtendedMuiCheckboxProps = Pick<
	MuiCheckboxProps,
	| 'checked'
	| 'disableRipple'
	| 'disabled'
	| 'indeterminate'
	| 'inputRef'
	| 'name'
	| 'required'
	| 'size'
	| 'tabIndex'
	| 'value'
	| 'onChange'
	| 'onClick'
>;

export interface CheckboxProps
	extends WithClasses<CheckboxClasses, ExtendedMuiCheckboxProps> {
	error?: string;
	hideErrorMessage?: boolean;
	indeterminateUnchecked?: boolean;
	label: React.ReactNode;
	psuedoDisabled?: boolean;
	ref?: React.ForwardedRef<HTMLDivElement>;
	slotProps?: {
		errorField?: Partial<Omit<ErrorFieldProps, 'error'>>;
		formControlLabel?: Partial<Omit<FormControlLabelProps, 'control'>>;
		checkbox?: Partial<Omit<MuiCheckboxProps, keyof ExtendedMuiCheckboxProps>>;
	};
}

export const CheckboxPrimitive = (
	{
		classes: _classes,
		className,
		checked,
		disabled,
		disableRipple,
		error,
		hideErrorMessage,
		indeterminate,
		indeterminateUnchecked,
		inputRef,
		label,
		name,
		psuedoDisabled,
		required,
		size,
		slotProps,
		tabIndex,
		onChange,
		onClick,
		...props
	}: CheckboxProps,
	ref: React.ForwardedRef<HTMLDivElement>,
) => {
	checked = checked === true;

	const ownerState: CheckboxOwnerState = {
		classes: _classes,
		className,
		indeterminateUnchecked,
		psuedoDisabled,
	};

	const classes = useUtilityClasses(ownerState);

	const handleChange = useConstCallback(
		(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
			// Sometimes a change event will come through that was not related to
			// interacting directly with the input
			// I cannot seem to recreate this in the tests yet, but this is an
			// important chack
			if (!event) return;
			onChange?.(event, checked);
		},
	);

	return (
		<CheckboxRoot
			{...props}
			ref={ref}
			className={clsx(classes.root, className)}
		>
			<FormControlLabel
				disabled={disabled}
				className={classes.formControlLabel}
				{...slotProps?.formControlLabel}
				control={
					<MuiCheckbox
						className={classes.checkbox}
						tabIndex={tabIndex}
						// disabled={disabled}
						{...slotProps?.checkbox}
						checked={checked}
						disableRipple={disableRipple}
						indeterminate={indeterminate}
						inputRef={inputRef}
						name={name}
						required={required}
						size={size}
						onChange={handleChange}
						onClick={onClick}
					/>
				}
				label={label}
			/>
			{!hideErrorMessage ? (
				<ErrorField
					className={classes.error}
					disabled={disabled}
					{...slotProps?.errorField}
					error={error}
				/>
			) : null}
		</CheckboxRoot>
	);
};

export const Checkbox = React.forwardRef(CheckboxPrimitive) as (
	props: CheckboxProps,
) => JSX.Element;

export default Checkbox;
